import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import ReactDOM from 'react-dom/client';
import React from 'react';
import LayoutResolver from '../layouts/LayoutResolver';
import { DEFAULT_LAYOUT_ID } from '../layouts/LayoutRegistry';
import { getDesignById } from '../designs/DesignRegistry';
import { getStyleById } from '../styles/StyleRegistry';
import { getBackgroundById } from '../backgrounds/BackgroundRegistry';
import { getBarStyleById } from '../barStyles/BarStyleRegistry';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import theme from '../theme'; // Import your base theme
import { StyleProvider } from '../context/StyleContext'; // Keep for style context if needed
import { BackgroundProvider } from '../context/BackgroundContext'; // Keep for background context
import { LayoutProvider } from '../context/LayoutContext';
import { DesignProvider } from '../context/DesignContext';
import { TypographyProvider } from '../context/TypographyContext';
import { SpecialEffectProvider } from '../context/SpecialEffectContext';
import { BarStyleProvider } from '../context/BarStyleContext'; // Import BarStyleProvider
import { LanguageProvider } from '../localization'; // Import LanguageProvider

// Service to generate and export PDFs
class PdfService {
  constructor() {
    this.pdfContainer = null;
    this.reactRoot = null; // Keep track of the React root
    this.initContainer();
  }

  // Initialize hidden container for PDF rendering
  initContainer() {
    if (!this.pdfContainer) {
      this.pdfContainer = document.createElement('div');
      this.pdfContainer.id = 'pdf-render-container';
      this.pdfContainer.style.position = 'absolute';
      this.pdfContainer.style.left = '-9999px'; // Off-screen
      this.pdfContainer.style.top = '0px';
      this.pdfContainer.style.width = '210mm';
      this.pdfContainer.style.minHeight = '297mm';
      this.pdfContainer.style.backgroundColor = 'white'; // Ensure background for canvas
      this.pdfContainer.style.margin = '0';
      this.pdfContainer.style.padding = '0';
      this.pdfContainer.style.overflow = 'hidden'; // Prevent unexpected scrollbars
      document.body.appendChild(this.pdfContainer);
      // Create React root once
      this.reactRoot = ReactDOM.createRoot(this.pdfContainer);
    }
  }

  // Clean up the container and React root
  cleanupContainer() {
    if (this.reactRoot) {
      this.reactRoot.unmount(); // Unmount React component
      this.reactRoot = null;
    }
    if (this.pdfContainer && this.pdfContainer.parentNode) {
      this.pdfContainer.parentNode.removeChild(this.pdfContainer);
      this.pdfContainer = null;
    }
    // Re-initialize for next potential use (optional, depends on lifecycle)
    this.initContainer();
  }

  // Generate PDF with multiple pages
  async generateMultiPagePdf(
    formData,
    pages, // Array of arrays (page sections)
    layoutId,
    activeDesign, // The full design object including colors
    typographySettings,
    styleId, // Pass selected style ID
    backgroundId, // Pass selected background ID
    specialEffectId, // Added prop
    barStyleId // Added barStyleId
  ) {
    if (!this.pdfContainer || !this.reactRoot) {
      console.error('PDF container not initialized.');
      this.initContainer(); // Try to re-initialize
      if (!this.pdfContainer || !this.reactRoot) {
         throw new Error('Failed to initialize PDF rendering container.');
      }
    }

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
      // Consider adding metadata
      // subject: 'Resume generated with PrivaCV',
      // title: `${formData?.personalInfo?.firstName || 'Resume'}.pdf`,
    });

    const totalPages = pages.length;

    try {
      for (let i = 0; i < totalPages; i++) {
        console.log(`Rendering PDF Page ${i + 1}/${totalPages}`);
        if (i > 0) {
          pdf.addPage();
        }

        // Render the current page content into the hidden container
        await this.renderPageContent(
          this.reactRoot,
          formData,
          pages[i], // Sections for this specific page
          i,
          totalPages,
          layoutId,
          activeDesign,
          typographySettings,
          styleId,
          backgroundId,
          specialEffectId, // Added prop
          barStyleId // Pass barStyleId
        );

        // Allow time for rendering and styles to apply
        await new Promise(resolve => setTimeout(resolve, 300)); // Adjust timeout as needed

        // Capture the rendered content to canvas
        // Ensure the container itself is the target for html2canvas
        const canvas = await html2canvas(this.pdfContainer, {
          scale: 2, // Adjust scale for quality vs performance
          useCORS: true,
          allowTaint: true,
          logging: false, // Set to true for debugging canvas issues
          backgroundColor: activeDesign?.colors?.background || '#ffffff', // Use design background or white
          width: this.pdfContainer.offsetWidth, // Explicit width based on container
          height: this.pdfContainer.offsetHeight, // Explicit height
          windowWidth: this.pdfContainer.offsetWidth,
          windowHeight: this.pdfContainer.offsetHeight,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95); // Use JPEG for smaller size, adjust quality
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      }

      return pdf;

    } catch (error) {
      console.error('Error during multi-page PDF generation:', error);
      throw error; // Re-throw error to be caught by the caller

    } finally {
       // Ensure cleanup happens even if there's an error
       // Optional: Keep container if generating multiple PDFs? For now, clean up.
       // this.cleanupContainer(); // Consider if cleanup is needed after each generation or globally
       // Clear the container content for the next render (if not cleaning up fully)
       if (this.reactRoot) {
         this.reactRoot.render(null); // Render null to clear content
       }
    }
  }

  // Render a single page using our layout system
  async renderPageContent(
    reactRoot,
    formData,
    pageSections, // Sections for THIS page
    pageNum,
    totalPages,
    layoutId,
    activeDesign, // Full design object
    typographySettings,
    styleId,
    backgroundId,
    specialEffectId, // Added prop
    barStyleId // Added barStyleId
  ) {
    return new Promise((resolve) => {
      // Resolve components from registries for PDF export
      const activeStyle = getStyleById(styleId);
      const backgroundData = getBackgroundById(backgroundId);
      const activeBackgroundComponent = backgroundData?.component;
      const activeBarStyle = getBarStyleById(barStyleId || 'stars');

      // Prepare minimal theme/context providers needed by LayoutResolver and its children
      // We pass static values, not full stateful contexts
      const pageRenderTheme = createTheme(theme, {
          // Potentially override theme aspects based on design if needed
          palette: {
             primary: { main: activeDesign?.colors?.primary || theme.palette.primary.main },
             secondary: { main: activeDesign?.colors?.secondary || theme.palette.secondary.main },
             // Add other palette colors if used directly by components
          },
          typography: typographySettings, // Apply typography directly if possible
      });

      // Determine the initial design ID for the provider
      const initialDesignId = activeDesign?.id || 'default-black'; // Use ID from activeDesign or fallback

      // Use requestAnimationFrame to ensure rendering happens in the next paint cycle
      requestAnimationFrame(() => {
        reactRoot.render(
          // StrictMode can sometimes cause double renders, remove if causing issues in PDF gen
          // <React.StrictMode>
            <ThemeProvider theme={pageRenderTheme}>
              <LanguageProvider>
               {/* Wrap with LayoutProvider, StyleProvider, BackgroundProvider, DesignProvider */}
               <DesignProvider initialDesign={initialDesignId}> 
                 <LayoutProvider initialLayout={layoutId || DEFAULT_LAYOUT_ID}> 
                   <TypographyProvider initialSettings={typographySettings}>
                     <StyleProvider initialStyleId={styleId}>
                        <BackgroundProvider initialBackgroundId={backgroundId}>
                          <SpecialEffectProvider initialSpecialEffectId={specialEffectId}>
                            <BarStyleProvider initialBarStyleId={barStyleId || 'stars'}> {/* Wrap with BarStyleProvider */}
                               <LayoutResolver
                                 formData={formData}
                                 selectedSections={pageSections} // Crucial: Use sections for the current page
                                 currentPage={pageNum}
                                 totalPages={totalPages}
                                 isPdfExport={true}
                                 measureMode={false} // Ensure this is false for actual rendering
                                 // Direct props for PDF export (bypass context)
                                 selectedLayout={layoutId}
                                 activeDesign={activeDesign}
                                 typographySettings={typographySettings}
                                 selectedStyle={styleId}
                                 activeStyle={activeStyle}
                                 selectedBackground={backgroundId}
                                 activeBackgroundComponent={activeBackgroundComponent}
                                 selectedSpecialEffectId={specialEffectId}
                                 activeBarStyle={activeBarStyle}
                               />
                            </BarStyleProvider>
                          </SpecialEffectProvider>
                        </BackgroundProvider>
                     </StyleProvider>
                   </TypographyProvider>
                 </LayoutProvider>
               </DesignProvider>
              </LanguageProvider>
            </ThemeProvider>
          // </React.StrictMode>
        );
        // Resolve the promise after initiating the render.
        // The actual waiting for render completion is handled by setTimeout in generateMultiPagePdf
        resolve();
      });
    });
  }

  // Main Export Function (called by UI)
  async exportPdf({
      formData,
      pages,
      layoutId,
      activeDesign,
      typographySettings,
      styleId,
      backgroundId,
      specialEffectId, // Added prop
      barStyleId // Added barStyleId
  }) {
      const loadingElement = this.showLoadingIndicator();
      document.body.style.cursor = 'wait';
      console.log(`PdfService: Exporting PDF with barStyleId: ${barStyleId || 'stars'}`); // Log barStyleId

      try {
          // Ensure container is ready
          this.initContainer();

          console.log('Generating multi-page PDF with data:', { layoutId, designId: activeDesign?.id, styleId, backgroundId, specialEffectId, pages });

          const pdf = await this.generateMultiPagePdf(
              formData,
              pages,
              layoutId || DEFAULT_LAYOUT_ID,
              activeDesign || getDesignById('default-black'), // Ensure valid design object using string literal
              typographySettings,
              styleId, // Pass styleId
              backgroundId, // Pass backgroundId
              specialEffectId || 'none', // Added prop, with fallback
              barStyleId || 'stars' // Pass barStyleId, default to 'stars'
          );

          // Generate filename
          const now = new Date();
          const dateStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
          const firstName = formData?.personalInfo?.firstName || 'Resume';
          const lastName = formData?.personalInfo?.lastName || '';
          const filename = `PrivaCV_${firstName}${lastName ? '_' + lastName : ''}_${dateStr}.pdf`;

          // Save the generated PDF
          pdf.save(filename);
          console.log('Multi-page PDF saved successfully.');
          return true; // Indicate success

      } catch (error) {
          console.error('PDF export failed:', error);
          alert(`PDF generation failed: ${error.message || 'Unknown error'}. Please check console for details.`);
          return false; // Indicate failure

      } finally {
          // Hide loading indicator and restore cursor
          this.hideLoadingIndicator(loadingElement);
          document.body.style.cursor = 'default';
          // Consider cleanup strategy - maybe clean up container here?
          // this.cleanupContainer(); // Uncomment if cleanup after each export is desired
      }
  }

  // UI Helper Methods
  showLoadingIndicator() {
      const loadingElement = document.createElement('div');
      loadingElement.style.position = 'fixed';
      loadingElement.style.top = '50%';
      loadingElement.style.left = '50%';
      loadingElement.style.transform = 'translate(-50%, -50%)';
      loadingElement.style.padding = '20px 30px';
      loadingElement.style.background = 'rgba(0,0,0,0.8)';
      loadingElement.style.color = 'white';
      loadingElement.style.borderRadius = '8px';
      loadingElement.style.zIndex = '10001'; // Ensure it's above everything
      loadingElement.style.textAlign = 'center';
      loadingElement.innerHTML = 'Generating PDF...<br/>Please wait';
      document.body.appendChild(loadingElement);
      return loadingElement;
  }

  hideLoadingIndicator(element) {
      if (element && element.parentNode) {
          element.parentNode.removeChild(element);
      }
  }
}

// Export service instance
const pdfService = new PdfService();
export default pdfService;